import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { ref, onValue } from "firebase/database";
import { FiArrowLeft, FiClock, FiMinusCircle, FiCheckCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Hook import kiya

const WithdrawalHistory = () => {
    const { t } = useTranslation(); // Translation function initialized
    const [activeTab, setActiveTab] = useState('pending');
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const savedUser = localStorage.getItem('user');
        if (!savedUser) {
            navigate('/login');
            return;
        }
        const currentUser = JSON.parse(savedUser);

        // Firebase path 'withdrawals' as per database screenshot
        const withdrawRef = ref(db, 'withdrawals');
        
        const unsubscribe = onValue(withdrawRef, (snapshot) => {
            const data = snapshot.val();
            const userHistory = [];
            
            if (data) {
                Object.keys(data).forEach(key => {
                    const record = data[key];
                    // Matching records for the logged-in user
                    if (record.username === currentUser.username) {
                        userHistory.push({ 
                            id: key, 
                            ...record 
                        });
                    }
                });
            }
            
            // Sorting: Newest on top
            setHistory(userHistory.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)));
            setLoading(false);
        }, (error) => {
            console.error("Firebase Error:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [navigate]);

    // Filter Logic for Pending and Success tabs
    const filteredData = history.filter(item => {
        const status = item.status?.toLowerCase();
        if (activeTab === 'pending') {
            return status === 'pending';
        } else {
            return status === 'success' || status === 'approved';
        }
    });

    return (
        <div style={styles.container}>
            {/* Header Section */}
            <div style={styles.header}>
                <FiArrowLeft onClick={() => navigate(-1)} size={24} style={styles.backBtn} />
                <h2 style={styles.title}>{t('withdrawal_history_title')}</h2>
                <div style={{width: 24}}></div>
            </div>

            {/* Professional Tab Switcher */}
            <div style={styles.tabContainer}>
                <div 
                    onClick={() => setActiveTab('pending')}
                    style={{
                        ...styles.tab, 
                        borderBottom: activeTab === 'pending' ? '3px solid #f3ba2f' : '3px solid transparent', 
                        color: activeTab === 'pending' ? '#f3ba2f' : '#888'
                    }}
                >
                    {t('tab_pending')}
                </div>
                <div 
                    onClick={() => setActiveTab('approved')}
                    style={{
                        ...styles.tab, 
                        borderBottom: activeTab === 'approved' ? '3px solid #f3ba2f' : '3px solid transparent', 
                        color: activeTab === 'approved' ? '#f3ba2f' : '#888'
                    }}
                >
                    {t('tab_success')}
                </div>
            </div>

            {/* List Section */}
            <div style={styles.listContainer}>
                {loading ? (
                    <div style={styles.noData}>
                         <p style={styles.emptyMsg}>{t('fetching_records')}</p>
                    </div>
                ) : filteredData.length > 0 ? (
                    filteredData.map((item) => (
                        <div key={item.id} style={styles.card}>
                            <div style={styles.cardIcon}>
                                {activeTab === 'approved' ? 
                                    <FiCheckCircle color="#0ecb81" size={22} /> : 
                                    <FiMinusCircle color="#ff4d4f" size={22} />
                                }
                            </div>
                            
                            <div style={styles.cardContent}>
                                <div style={styles.topRow}>
                                    <span style={styles.amount}>-{item.amount} USDT</span>
                                    <span style={{
                                        ...styles.statusBadge, 
                                        background: activeTab === 'pending' ? '#fff7e6' : '#f6ffed', 
                                        color: activeTab === 'pending' ? '#d46b08' : '#389e0d',
                                        borderColor: activeTab === 'pending' ? '#ffd591' : '#b7eb8f'
                                    }}>
                                        {item.status?.toUpperCase()}
                                    </span>
                                </div>

                                {/* Full Address: Placed directly under amount, left-aligned */}
                                <div style={styles.fullAddress}>
                                    <span style={{color: '#999'}}>{t('address_prefix')}:</span> {item.address}
                                </div>

                                <div style={styles.bottomRow}>
                                    <span style={styles.date}>
                                        {item.processedAt ? new Date(item.processedAt).toLocaleString() : (item.timestamp ? new Date(item.timestamp).toLocaleString() : t('processing_status'))}
                                    </span>
                                    <span style={styles.walletName}>{item.walletName || 'Network'}</span>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div style={styles.noData}>
                        <FiClock size={50} color="#e0e0e0" />
                        <p style={styles.emptyMsg}>
                            {t('no_history_found', { status: activeTab === 'pending' ? t('tab_pending') : t('tab_success') })}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

const styles = {
    container: { 
        background: '#fcfcfc', 
        minHeight: '100vh', 
        padding: '20px 15px' 
    },
    header: { 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '25px' 
    },
    backBtn: { 
        cursor: 'pointer', 
        color: '#333' 
    },
    title: { 
        fontSize: '20px', 
        fontWeight: '700', 
        color: '#1a1a1a',
        margin: 0 
    },
    tabContainer: { 
        display: 'flex', 
        background: '#fff', 
        borderRadius: '12px', 
        marginBottom: '20px', 
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)' 
    },
    tab: { 
        flex: 1, 
        textAlign: 'center', 
        padding: '14px', 
        fontSize: '15px', 
        fontWeight: '600', 
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    listContainer: { 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '15px' 
    },
    card: { 
        background: '#fff', 
        padding: '16px', 
        borderRadius: '16px', 
        display: 'flex', 
        gap: '15px', 
        alignItems: 'flex-start', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        border: '1px solid #f0f0f0'
    },
    cardIcon: { 
        background: '#fdfdfd', 
        padding: '10px', 
        borderRadius: '12px',
        marginTop: '2px'
    },
    cardContent: { 
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    topRow: { 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '6px' 
    },
    amount: { 
        fontWeight: '700', 
        fontSize: '17px', 
        color: '#ff4d4f' 
    },
    fullAddress: {
        fontSize: '12px',
        color: '#444',
        wordBreak: 'break-all', 
        backgroundColor: '#f9f9f9',
        padding: '6px 8px',
        borderRadius: '6px',
        marginBottom: '8px',
        lineHeight: '1.4',
        textAlign: 'left'
    },
    bottomRow: { 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
    },
    date: { 
        fontSize: '11px', 
        color: '#999' 
    },
    walletName: { 
        fontSize: '12px', 
        color: '#555', 
        fontWeight: '600',
        textTransform: 'uppercase'
    },
    statusBadge: { 
        fontSize: '10px', 
        padding: '3px 8px', 
        borderRadius: '6px', 
        fontWeight: '700',
        border: '1px solid'
    },
    noData: { 
        textAlign: 'center', 
        marginTop: '100px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px'
    },
    emptyMsg: { 
        color: '#bbb', 
        fontSize: '15px',
        fontWeight: '500'
    }
};

export default WithdrawalHistory;